import { signal } from '@angular/core';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import {
  EsriViewType,
  ExtendedMap,
  JmkwEnvironment,
  JmkwThemeConfig,
  METRIC_DEFAULT,
  PluginConfigurationRecord,
  PluginMap,
  ViewConfig,
  WidgetParams
} from '@jeppesen-foreflight/jmkw-types';
import isEqual from 'lodash-es/isEqual';
import { SignalStore, signalStore } from './store/signal-store';

const views: Record<EsriViewType, ViewConfig> = {
  '2D': {
    view: signal({} as MapView),
    visible: true
  },
  '3D': {
    view: signal({} as SceneView),
    visible: false
  }
};

export let store: SignalStore<typeof initialStoreState>;

const env = {
  production: false,
  basemapDomain: '',
  telemetryKey: '',
  unitConfigs: METRIC_DEFAULT, // ExtendedMap<JmkwUnitConfig>,
  hideToolbar: false,
  hideWidgetBar: false,
  hideAttributionBar: false,
  hideZoomButtons: false,
  bypassAuth: false,
  themeConfig: {
    defaultTheme: 'default-light',
    enabledThemes: ['default-light', 'default-dark'],
    enabledBasemaps: ['jmkw-dark-basemap', 'jmkw-light-basemap']
  } as JmkwThemeConfig
};

const initialStoreState = {
  map: signal<Map>(undefined as unknown as Map),
  views,
  state: {
    mapLoaded: false,
    layersLoaded: false,
    viewsLoaded: false,
    initialized: false
  },
  bypassAuth: false,
  pluginMap: signal(new PluginMap(), {
    equal: (a, b) => {
      // Handle null/undefined cases safely
      if (!a && !b) return true;
      if (!a || !b) return false;

      // Both are defined, check size and keys
      return (
        a.size === b.size && isEqual(Array.from(a.keys()), Array.from(b.keys()))
      );
    }
  }),
  pluginConfigurations: signal({} as PluginConfigurationRecord),
  activeWidgetMap: signal(new ExtendedMap<WidgetParams>()),
  env
};

export function configureSignalStore(config: Partial<JmkwEnvironment> = {}) {
  return () => {
    // Handle backward compatibility: if domain is provided but basemapDomain is not, use domain
    const finalConfig = { ...config };
    if (finalConfig.domain && !finalConfig.basemapDomain) {
      console.warn('[JmkwStore] DEPRECATED: Using "domain" property. Please migrate to "basemapDomain" - "domain" will be removed in a future version.');
      finalConfig.basemapDomain = finalConfig.domain;
    }

    store = signalStore({
      ...initialStoreState,
      env: {
        ...initialStoreState.env,
        ...finalConfig
      }
    });
  };
}
